/**
 * MemoryPage — `/memory`. Память проекта: контекст (инжектится в AI) + журнал
 * решений. Чем полнее контекст — тем точнее AI «сразу в курсе».
 */

import { useEffect, useState } from 'react';
import { usePageHeader } from '@/components/layout/pageHeader';
import { Button, LoadingBlock, ErrorBlock } from '@/shared/ui';
import {
  useProjectContext,
  useSaveContext,
  useDecisions,
  useAddDecision,
} from '@/hooks/api/useMemory';

export function MemoryPage() {
  usePageHeader({ title: 'Память проекта', subtitle: 'Контекст и решения — AI учитывает это в чате' });

  const ctx = useProjectContext();
  const saveCtx = useSaveContext();
  const decisions = useDecisions();
  const addDecision = useAddDecision();

  const [text, setText] = useState('');
  const [summary, setSummary] = useState('');
  const [expected, setExpected] = useState('');

  useEffect(() => {
    if (ctx.data) setText(ctx.data.context);
  }, [ctx.data]);

  if (ctx.isLoading) return <LoadingBlock label="Загружаю память…" />;
  if (ctx.isError) return <ErrorBlock text={(ctx.error as Error)?.message} onRetry={() => void ctx.refetch()} />;

  return (
    <div className="col gap20" style={{ maxWidth: 880 }}>
      <section className="card col" style={{ gap: 10 }}>
        <div className="row spread">
          <h3 style={{ margin: 0 }}>Контекст проекта</h3>
          <Button
            variant="primary"
            size="sm"
            icon="Check"
            disabled={saveCtx.isPending || text === ctx.data?.context}
            onClick={() => saveCtx.mutate(text)}
          >
            {saveCtx.isPending ? 'Сохраняю…' : 'Сохранить'}
          </Button>
        </div>
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>
          Этот текст добавляется в системный промпт AI при каждом диалоге. Опишите продукт, ICP,
          цели, ограничения (CPL ≤ 15к), особенности трекинга.
        </p>
        <textarea
          className="input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={10}
          style={{ resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }}
          placeholder="Например: KOMMA — корпоративное суперприложение (мессенджер+ВКС+портал)…"
        />
      </section>

      <section className="card col" style={{ gap: 12 }}>
        <h3 style={{ margin: 0 }}>Журнал решений</h3>
        <div className="row wrap gap8" style={{ alignItems: 'flex-end' }}>
          <div className="col grow" style={{ gap: 4, minWidth: 240 }}>
            <label className="muted" style={{ fontSize: 12 }}>Решение</label>
            <input className="input" value={summary} onChange={(e) => setSummary(e.target.value)}
              placeholder="Свернули Тест_Конкуренты — 0 конверсий" />
          </div>
          <div className="col grow" style={{ gap: 4, minWidth: 200 }}>
            <label className="muted" style={{ fontSize: 12 }}>Ожидаемый результат</label>
            <input className="input" value={expected} onChange={(e) => setExpected(e.target.value)}
              placeholder="−190к/мес расхода впустую" />
          </div>
          <Button
            variant="outline"
            size="sm"
            icon="Plus"
            disabled={!summary.trim() || addDecision.isPending}
            onClick={() =>
              addDecision.mutate(
                { summary, expected_outcome: expected },
                { onSuccess: () => { setSummary(''); setExpected(''); } },
              )
            }
          >
            Записать
          </Button>
        </div>

        {decisions.data?.length ? (
          <div className="col gap8">
            {decisions.data.map((d) => (
              <div key={d.id} className="row" style={{ gap: 10, alignItems: 'flex-start' }}>
                <span className="mono muted" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                  {d.created_at.slice(0, 10)}
                </span>
                <div className="col" style={{ gap: 2 }}>
                  <span style={{ fontWeight: 600, fontSize: 14 }}>{d.summary}</span>
                  {d.expected_outcome && (
                    <span className="muted" style={{ fontSize: 12.5 }}>→ {d.expected_outcome}</span>
                  )}
                  <span className="muted mono" style={{ fontSize: 11 }}>{d.author}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>Решений пока нет.</p>
        )}
      </section>
    </div>
  );
}
